<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          title="Add Attendance Schedule"
          btnTitle="Add New"
          :showBtn="true"
          :showAddNew="false"
          @onClickCloseButton="navigateToListPage"
      />
    </div>
    <div class="my-2 px-2">
      <DateQuerySetter
          btnTitle="Go"
          @onClickGo="onClickToGo"
          class="tds-vds-query-setter"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Branch</label>
          <v-select
              placeholder="Select Branch"
              v-model="branchId"
              :options="branchList"
              label="name"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Department</label>
          <v-select
              placeholder="Select Department"
              v-model="departmentId"
              :options="departmentList"
              label="name"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Designation</label>
          <v-select
              placeholder="Select Designation"
              v-model="designationId"
              :options="designationList"
              label="name"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="col-12">
      <div class="card shadow-sm border-0">
        <div class="card-body" style="min-height: 300px">
          <div class="table-responsive border rounded">
            <table class="table table-bordered align-middle text-center mb-0">
              <thead class="table-light">
              <tr>
                <th class="sticky-col">Employee</th>
                <th
                    v-for="date in datePeriods"
                    :key="date"
                    :colspan="policyMasterList.length"
                    class="bg-light fw-semibold text-secondary"
                >
                  {{ formatDate(date) }}
                </th>
              </tr>
              <tr>
                <th class="sticky-col"></th>
                <template v-for="date in datePeriods" :key="date">
                  <th class="shift-header morning"
                      v-for="shift in policyMasterList"
                      :key="date + '_' + shift.id"
                  >
                     {{ shift.name }} <br>
                  </th>
                </template>
              </tr>
              </thead>

              <tbody>
              <tr v-for="emp in contactProfileList" :key="emp.id">
                <td class="bg-light fw-medium sticky-col">
                  {{ emp.name }}({{ emp.serial_no }})
                </td>

                <template v-for="date in datePeriods" :key="date">
                  <td
                      v-for="shift in policyMasterList"
                      :key="shift.id + date"
                      class="text-center drop-zone"
                      @dragover.prevent
                      @drop="handleDrop($event, emp.id, date, shift.id)"
                      :class="{ 'drag-over': isDragOver(emp.id, date, shift.id) }"
                      @dragenter="handleDragEnter($event, emp.id, date, shift.id)"
                      @dragleave="handleDragLeave($event)"
                  >
                <span
                    v-for="schedule in emp.schedule_data"
                    :key="schedule.attendance_policy_general_id"
                >
                  <div
                      v-if="
                      schedule.date === date &&
                      schedule.attendance_policy_master_id === shift.id
                    "
                      class="schedule-item bg-primary"
                      draggable="true"
                      @dragstart="handleDragStart($event, emp.id, schedule, date, shift.id)"
                      @dragend="handleDragEnd"
                  >
                    <p class="m-0 p-0 text-white">
                      {{ schedule.label }}
                    </p>
                  </div>
                </span>
                  </td>
                </template>
              </tr>
              </tbody>
            </table>
            <p v-if="contactProfileList.length < 1">No Data Found</p>
          </div>

          <div class="mt-2 d-flex justify-content-end px-2 gap-1 flex-wrap pb-2">
            <hr class="hr-full-width mb-1">
            <button :disabled="loading" @click="handleSubmit()"
                    class="btn btn-primary">
              Save
            </button>
          </div>
        </div>
      </div>
    </div>

    <Loader v-if="loading"/>

  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import { computed } from 'vue';
import handleDropdowns from "@/services/modules/dropdowns";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const tableItems = ref([]);
const contactProfileList = ref([]);
const policyMasterList = ref([]);
const datePeriods = ref([])
const draggedData = ref(null);
const dragOverTarget = ref(null);
const branchId = ref('');
const departmentId = ref('');
const designationId = ref('');
const matchedSchedulesData = ref([]);
let departmentList = ref([])
let branchList = ref([])
let designationList = ref([])

const params = ref({
  offset: 10,
});
const token = computed(() => localStorage.getItem('token'));

const {
  fetchAttendanceScheduleCreate,
  storeAttendanceSchedule
} = handleHrPayroll()
const { fetchDropdownList } = handleDropdowns()

const generateMatchedSchedulesData = () => {
  const matched = [];

  const contactList = Array.isArray(contactProfileList.value)
      ? contactProfileList.value
      : Object.values(contactProfileList.value);

  contactList.forEach(emp => {
    datePeriods.value.forEach(date => {
      policyMasterList.value.forEach(shift => {
        emp.schedule_data.forEach(schedule => {
          if (schedule.date === date && schedule.attendance_policy_master_id === shift.id) {
            matched.push({
              contact_profile_id: schedule.contact_profile_id,
              shifted_date:  schedule.date,
              schedule_date: schedule.schedule_date || schedule.date,
              attendance_policy_master_id: schedule.attendance_policy_master_id,
              label: schedule.label,
              adjust_contact_profile_id: schedule.adjust_contact_profile_id || null,
              attendance_schedule_id: schedule.attendance_schedule_id || null
            });
          }
        });
      });
    });
  });

  matchedSchedulesData.value = matched;
};

const handleDragStart = (event, empId, schedule, date, shiftId) => {
  draggedData.value = {
    empId,
    schedule,
    originalDate: date,
    shiftId
  };
  event.target.style.opacity = '0.5';
};

const handleDragEnd = (event) => {
  event.target.style.opacity = '1';
  draggedData.value = null;
  dragOverTarget.value = null;
};

const handleDragEnter = (event, empId, date, shiftId) => {
  if (draggedData.value) {
    dragOverTarget.value = { empId, date, shiftId };
  }
};

const handleDragLeave = (event) => {
  if (!event.currentTarget.contains(event.relatedTarget)) {
    dragOverTarget.value = null;
  }
};

const isDragOver = (empId, date, shiftId) => {
  return dragOverTarget.value &&
      dragOverTarget.value.empId === empId &&
      dragOverTarget.value.date === date &&
      dragOverTarget.value.shiftId === shiftId;
};

const getDayFromDate = (dateStr) => {
  const date = new Date(dateStr);
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
};

const calculateLabel = (inTime, workingHours) => {
  const [hours, minutes] = inTime.split(':').map(Number);
  const startDate = new Date();
  startDate.setHours(hours, minutes, 0, 0);

  const endDate = new Date(startDate.getTime() + workingHours * 60 * 60 * 1000);

  const formatTime = (date) => {
    let h = date.getHours();
    const m = date.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    const mm = m.toString().padStart(2, '0');
    return `${h}:${mm} ${ampm}`;
  };

  return `${formatTime(startDate)} - ${formatTime(endDate)}`;
};

const handleDrop = (event, targetEmpId, targetDate, targetShiftId) => {
  event.preventDefault();

  if (!draggedData.value) return;

  const { empId, schedule, originalDate, shiftId } = draggedData.value;

  const contactList = Array.isArray(contactProfileList.value)
      ? contactProfileList.value
      : Object.values(contactProfileList.value);

  const sourceEmployee = contactList.find(emp => emp.id === empId);
  if (!sourceEmployee) return;

  const targetEmployee = contactList.find(emp => emp.id === targetEmpId);
  if (!targetEmployee) return;

  const existingSchedule = targetEmployee.schedule_data.find(
      s => s.date === targetDate &&
          s.attendance_policy_master_id === targetShiftId
  );

  if (existingSchedule &&
      !(existingSchedule.date === schedule.date &&
          existingSchedule.attendance_policy_master_id === schedule.attendance_policy_master_id &&
          empId === targetEmpId)) {
    showError('This cell already has a schedule. Please choose an empty cell.');
    dragOverTarget.value = null;
    return;
  }

  const scheduleIndex = sourceEmployee.schedule_data.findIndex(
      s => s.date === schedule.date &&
          s.attendance_policy_master_id === schedule.attendance_policy_master_id
  );

  if (scheduleIndex === -1) return;

  const originalSchedule = sourceEmployee.schedule_data[scheduleIndex];
  sourceEmployee.schedule_data.splice(scheduleIndex, 1);
  const updatedSchedule = { ...originalSchedule };

  if (!updatedSchedule.schedule_date) {
    updatedSchedule.schedule_date = originalSchedule.date;
  }

  updatedSchedule.date = targetDate;
  updatedSchedule.attendance_policy_master_id = targetShiftId;

  const targetShift = policyMasterList.value.find(shift => shift.id === targetShiftId);
  const hasTargetShift = targetShift !== undefined;

  let targetPolicy = null;
  if (hasTargetShift) {
    targetPolicy = targetShift.attendance_policy_generals.find(
        policy => policy.day === getDayFromDate(targetDate)
    );
  }

  const hasTargetPolicy = targetPolicy !== null;

  if (hasTargetPolicy && targetPolicy) {
    const inTime = targetPolicy.in_time;
    const workingHours = targetPolicy.working_hours;
    updatedSchedule.label = calculateLabel(inTime, workingHours);
    updatedSchedule.attendance_policy_general_id = targetPolicy.id;
  }

  const isDifferentEmployee = empId !== targetEmpId;
  const isReturningToAdjustedEmployee = updatedSchedule.adjust_contact_profile_id === targetEmpId;
  const needsNewAdjustment = !updatedSchedule.adjust_contact_profile_id;

  if (isDifferentEmployee && isReturningToAdjustedEmployee) {
    updatedSchedule.adjust_contact_profile_id = null;
    updatedSchedule.contact_profile_id = targetEmpId;
  }

  if (isDifferentEmployee && !isReturningToAdjustedEmployee && needsNewAdjustment) {
    updatedSchedule.adjust_contact_profile_id = empId;
  }

  if (isDifferentEmployee && !isReturningToAdjustedEmployee) {
    updatedSchedule.contact_profile_id = targetEmpId;
  }

  if (!isDifferentEmployee) {
    updatedSchedule.contact_profile_id = targetEmpId;
  }

  targetEmployee.schedule_data.push(updatedSchedule);

  generateMatchedSchedulesData();

  dragOverTarget.value = null;
};

const handleSubmit = () => {
  loading.value = true;
  let payload = {}
  payload.schedules = matchedSchedulesData.value;
  let query = `?company_id=${route.params.companyId}`;
  storeAttendanceSchedule(payload, query)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        getAttendanceScheduleData();
      })
      .catch(err => {
        console.log(err)
      }).finally(() => {
       loading.value = false
  })
}

const formatDate = (date) => {
  const d = new Date(date);
  const dayName = d.toLocaleDateString("en-GB", { weekday: "short" });
  const formatted = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });

  return `${formatted}, ${dayName}`;
};

const onClickToGo = () => {
  getAttendanceScheduleData();
}
function navigateToListPage() {
  router.push({name: `attendance-schedule`, params: route.params, query: route.query});
}

const getAttendanceScheduleData = async () => {
  const query = getQuery();
  loading.value = true
  contactProfileList.value = []
  policyMasterList.value = []
  datePeriods.value = []

  await fetchAttendanceScheduleCreate(query).then(res => {
    if (!res.status){
      return
    }
    let data = res.data;

    contactProfileList.value = data.contact_profiles;
    policyMasterList.value = data.attendance_policy_master;
    datePeriods.value = data.periods;
    generateMatchedSchedulesData();
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const getQuery = () => {
  let query = `?company_id=${route.params.companyId}&is_all=1`;
  if (route.query.start) query += '&start_date=' + route.query.start;
  if (route.query.end) query += '&end_date=' + route.query.end;
  if (branchId.value) query += '&branch_id=' + branchId.value.id;
  if (departmentId.value) query += '&department_id=' + departmentId.value.id;
  if (designationId.value) query += '&designation_id=' + designationId.value.id;
  return query;
}

const getDropdownList = async () => {
  let query = getQuery();
  query += '&is_branch=1&is_department=1&is_designation=1'

  branchList.value = []
  departmentList.value = []

  await fetchDropdownList(query).then(res => {
    if (!res.status){
      return
    }

    let data = res.data;
    branchList.value = data.branch_list;
    departmentList.value = data.department_list;
    designationList.value = data.designation_list;
  }).catch(err => {
    console.log(err)
  })
}

onMounted(() => {
  Promise.all([
    getDropdownList(),
  ])
})

</script>

<style scoped>
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 10;
  background: white;
}

.drop-zone {
  min-height: 60px;
  transition: background-color 0.2s;
}

.drop-zone.drag-over {
  background-color: #e3f2fd;
  border: 2px dashed #2196f3;
}

.schedule-item {
  cursor: move;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  transition: opacity 0.2s;
}

.schedule-item:hover {
  background-color: #e9ecef;
}

.table-responsive {
  max-height: 600px;
  overflow: auto;
}

.shift-header {
  min-width: 120px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>